import EditLocationIcon from '@mui/icons-material/EditLocation';
import ErrorIcon from '@mui/icons-material/Error';
import {
  Button,
  CardContent,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { JobsiteDTO } from '../api/generated';
import { useUpdateUserMutation } from '../api/mutations/user';
import { MenuBar } from '../components/MenuBar';
import { useSnackbar } from '../components/SnackbarProvider';
import { useUserContext } from '../UserContext';
import {
  clearPendingJobsiteRedirect,
  createPendingJobsiteRedirect,
  getSmartNavigationRoute,
  isPendingJobsiteRedirect,
} from '../utils/navigationUtils';
import { queryClient } from '../api/queryClient';
import { userKeys } from '../api/queries/user';
import { getUserPathId } from '../utils';

const redirectDelay = 2000;

export function JobsiteSelect() {
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const { data: user } = useUserContext();
  const { t } = useTranslation();
  const updateUserMutation = useUpdateUserMutation();

  async function handleJobsiteClick(jobsite: JobsiteDTO) {
    const pathId = getUserPathId(user);
    if (!pathId) return;

    if (user?.preferredJobsite?.id === jobsite.id) {
      return;
    }

    const previousJobsite = user?.preferredJobsite;
    const redirectToken = createPendingJobsiteRedirect();
    await updateUserMutation.mutateAsync(
      {
        authId: pathId,
        data: {
          preferredJobsiteId: jobsite.id,
        },
        showSuccessSnackbar: false,
      },
      {
        onSuccess: async () => {
          await queryClient.invalidateQueries({ queryKey: userKeys.read({ authId: pathId }) });
          if (!isPendingJobsiteRedirect(redirectToken)) return;

          const targetRoute = getSmartNavigationRoute(
            previousJobsite,
            jobsite,
            localStorage.getItem('lastVisitedRoute')
          );
          showSnackbar('location.changeFavorite', 'success', jobsite.name);
          await new Promise((resolve) => setTimeout(resolve, redirectDelay));
          if (!isPendingJobsiteRedirect(redirectToken)) return;

          showSnackbar('location.redirecting', 'info');
          await new Promise((resolve) => setTimeout(resolve, redirectDelay));
          if (!isPendingJobsiteRedirect(redirectToken)) return;

          clearPendingJobsiteRedirect(redirectToken);
          navigate(targetRoute);
        },
        onError: (err) => {
          clearPendingJobsiteRedirect(redirectToken);
          showSnackbar(err.message || 'error.default', 'error');
        },
      }
    );
  }

  React.useEffect(() => {
    if (!user?.preferredJobsite) showSnackbar('location.noFavoriteSelected', 'info');
  }, [showSnackbar, user?.preferredJobsite]);

  return (
    <Box>
      <MenuBar />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          paddingTop: '20px',
          paddingLeft: '20px',
          height: '80vh',
          margin: 'auto',
          width: { xs: 'calc(100% - 40px)', lg: 'calc(100% - 240px - 40px)' },
          top: 64,
          position: 'relative',
        }}
      >
        {updateUserMutation.isPending && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1000,
              borderRadius: '10px',
            }}
          >
            <CircularProgress size={60} />
          </Box>
        )}
        <Card
          elevation={8}
          sx={{
            borderRadius: '10px',
            width: '40%',
            height: '80%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {user?.jobsites && user?.jobsites?.length > 0 ? (
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-start',
                alignItems: 'center',
                gap: 2,
                flexGrow: 1,
              }}
            >
              <EditLocationIcon fontSize='large' />
              <Typography variant='h6' textAlign={'center'}>
                {t('location.select')}
              </Typography>
              <List sx={{ width: '100%' }}>
                {user?.jobsites.map((v) => (
                  <ListItem disablePadding key={v.name}>
                    <ListItemButton onClick={() => handleJobsiteClick(v)} sx={{ width: '100%' }}>
                      <ListItemText
                        primary={v.name}
                        primaryTypographyProps={{
                          textAlign: 'center',
                          fontWeight: user.preferredJobsite?.name === v.name ? '600' : '400',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          ) : (
            <>
              <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center', flexGrow: 1 }}>
                <ErrorIcon fontSize='large' />
                <Typography variant='h6' textAlign={'center'} display='block'>
                  {t('location.noneAvailable')}
                </Typography>
                <Typography variant='h6' textAlign={'center'} display='block'>
                  {t('location.administratorActionNeeded')}
                </Typography>
              </CardContent>
              {user?.admin && user?.jobsites && user?.jobsites.length === 0 && (
                <Button
                  sx={{
                    margin: 2,
                    marginBottom: 6,
                    padding: 1,
                    paddingLeft: 2,
                    paddingRight: 2,
                    borderRadius: 2,
                    backgroundColor: 'primary.light',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                    alignSelf: 'center',
                  }}
                  onClick={() => navigate('/user-admin')}
                >
                  <Typography variant='button' textAlign='center'>
                    <Box
                      component='span'
                      sx={{
                        cursor: 'pointer',
                        color: '#ffffff',
                      }}
                    >
                      {t('location.info')}
                    </Box>
                    .
                  </Typography>
                </Button>
              )}
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
}
